import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchAssetSummaryData } from '../../../api/fetchAssets';
import type { Asset } from '../../../models/asset';

export interface AssetAccount {
    key: string;
    name: string;
}

export interface AccountOption extends AssetAccount {
    count: number;
}

const CHUNK_SIZE = 2000;
const CONCURRENCY = 3;
const UPDATE_EVERY_CHUNKS = 5;
const ROWS_REFRESH_MS = 1000;
const accountCache = new Map<string, AssetAccount | null>();

const toAccount = (accountName?: string): AssetAccount | null => {
    const name = accountName && accountName !== '-' ? accountName.trim() : '';
    if (!name) {
        return null;
    }
    return { key: name, name };
};

export const clearAssetAccountsCache = () => accountCache.clear();

const useAssetAccounts = (assets: Asset[], enabled = true, rowAccountOf?: (assetId: string) => string | undefined) => {
    const [version, setVersion] = useState(0);
    const [loading, setLoading] = useState(false);
    const [rowsTick, setRowsTick] = useState(0);
    const rowAccountRef = useRef(rowAccountOf);
    const lastRowsRefresh = useRef(0);
    rowAccountRef.current = rowAccountOf;

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const wait = Math.max(0, lastRowsRefresh.current + ROWS_REFRESH_MS - Date.now());
        const timer = window.setTimeout(() => {
            lastRowsRefresh.current = Date.now();
            setRowsTick(value => value + 1);
        }, wait);
        return () => window.clearTimeout(timer);
    }, [rowAccountOf, enabled]);

    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        const missing = assets.filter(asset => !accountCache.has(asset.id)).map(asset => asset.id);
        if (!missing.length) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        const chunks: string[][] = [];
        for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
            chunks.push(missing.slice(i, i + CHUNK_SIZE));
        }

        let pendingUpdates = 0;
        const worker = async () => {
            while (chunks.length && !cancelled) {
                const ids = chunks.shift();
                if (!ids) {
                    return;
                }
                const data = await fetchAssetSummaryData(ids);
                if (cancelled || !data.length) {
                    continue;
                }
                const byId = new Map(data.map(item => [item.id, item]));
                for (const id of ids) {
                    const item = byId.get(id);
                    accountCache.set(id, toAccount(item?.accountName));
                }
                pendingUpdates += 1;
                if (pendingUpdates >= UPDATE_EVERY_CHUNKS) {
                    pendingUpdates = 0;
                    setVersion(value => value + 1);
                }
            }
        };

        Promise.all(Array.from({ length: CONCURRENCY }, worker)).then(() => {
            if (!cancelled) {
                setVersion(value => value + 1);
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [assets, enabled]);

    const accountByAsset = useMemo(() => {
        const map = new Map<string, AssetAccount>();
        if (!enabled) {
            return map;
        }
        const rowAccount = rowAccountRef.current;
        for (const asset of assets) {
            const account = toAccount(rowAccount?.(asset.id)) ?? accountCache.get(asset.id);
            if (account) {
                map.set(asset.id, account);
            }
        }
        return map;
    }, [assets, version, rowsTick, enabled]);

    const accounts = useMemo<AccountOption[]>(() => {
        const byKey = new Map<string, AccountOption>();
        for (const account of accountByAsset.values()) {
            const current = byKey.get(account.key);
            byKey.set(account.key, current ? { ...current, count: current.count + 1 } : { ...account, count: 1 });
        }
        return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [accountByAsset]);

    const accountNameByKey = useMemo(() => new Map(accounts.map(account => [account.key, account.name])), [accounts]);

    return { accountByAsset, accounts, accountNameByKey, loading };
};

export default useAssetAccounts;
